import { useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { useGetProfileDetailsQuery } from "~/graphql/supergraphHooks";
import EditEntityForm from "../../Profiles/common/EditAction/EditEntityForm";
import EditIndividualForm from "../../Profiles/common/EditAction/EditIndividualForm";

interface Props {
  caseId: number;
  programId: number;
}

const PaymentProfileEditComponent = ({ caseId, programId }: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { profileId: profileIdParam } = useParams<{ profileId: string }>();
  const profileId = Number(profileIdParam);
  const hasProfileId = Number.isInteger(profileId) && profileId > 0;

  const isFullScreenContext = location.pathname.includes("full-screen");

  const baseUrl = isFullScreenContext
    ? location.pathname.split("/edit/profile")[0]
    : `${location.pathname.split("/profile")[0]}`;

  const profileDetailsResults = useGetProfileDetailsQuery({
    variables: {
      profileId: profileId,
      programId: programId,
    },
    skip: !hasProfileId,
    fetchPolicy: "no-cache",
  });

  const navigateToBaseUrl = () => {
    history.push(baseUrl);
  };

  // "/profile-edit/abc" still matches :profileId. Skipping the query would leave
  // QueryLoader with no data and render the form with an undefined profile, whose
  // submit path CREATES a profile instead of editing one. Leave before that.
  useEffect(() => {
    if (!hasProfileId) {
      history.replace(baseUrl);
    }
  }, [hasProfileId, history, baseUrl]);

  if (!hasProfileId) {
    return null;
  }

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header onExit={navigateToBaseUrl} title={"Payment beneficiary"} />
      <QueryLoader result={profileDetailsResults}>
        {profileDetailsResults.data?.complianceProfile?.complianceObjectType ===
        ComplianceObjectType.Individual ? (
          <EditIndividualForm
            caseId={caseId}
            complianceProfile={profileDetailsResults.data?.complianceProfile}
            programId={programId}
            isBeneficiaryProfile
            onCancel={navigateToBaseUrl}
            onCompleted={navigateToBaseUrl}
          />
        ) : (
          <EditEntityForm
            caseId={caseId}
            complianceProfile={profileDetailsResults.data?.complianceProfile}
            programId={programId}
            isBeneficiaryProfile
            onCancel={navigateToBaseUrl}
            onCompleted={navigateToBaseUrl}
          />
        )}
      </QueryLoader>
    </FullscreenTakeover>
  );
};

export default PaymentProfileEditComponent;
